<template>
	<div class="">
		<v-card-title v-if="title" class="d-flex align-center">
			{{ title }}
			<v-icon color="info" @click="load">$reload</v-icon>
		</v-card-title>
		<v-card-title class="ptxxx-0">
			<v-form @submit.prevent="search" class="form-inline">
				<div class="d-flex flex-wrap align-center">
					<v-text-field
						outlined
						class="search-field-name mr-2"
						dense
						v-model="query.name"
						label="名称"
						hide-details
					/>
					<v-text-field
						outlined
						class="search-field-short mr-2"
						dense
						v-model="query.code"
						label="编码"
						hide-details
					/>
					<v-select
						outlined
						class="mr-2"
						dense
						label="状态"
						v-model="query.status"
						:items="refs['BeanStatus']"
						hide-details
						clearable
					></v-select>
					<v-select
						outlined
						class="mr-2"
						dense
						label="类型"
						v-model="query.kind"
						:items="kindItems"
						item-title="title"
						item-value="value"
						hide-details
						clearable
						style="max-width: 8rem"
					></v-select>
					<v-select
						outlined
						class="mr-2"
						dense
						label="产地"
						v-model="filterOrigins"
						:items="originItems"
						item-title="title"
						item-value="value"
						multiple
						chips
						closable-chips
						hide-details
						clearable
						style="max-width: 14rem"
					></v-select>
					<v-combobox
						outlined
						class="mr-2"
						dense
						label="处理法"
						v-model="filterProcesses"
						:items="processItems"
						multiple
						chips
						closable-chips
						hide-details
						clearable
						style="max-width: 14rem"
					></v-combobox>
					<v-combobox
						outlined
						class="mr-2"
						dense
						label="品种"
						v-model="filterVarieties"
						:items="varietyItems"
						multiple
						chips
						closable-chips
						hide-details
						clearable
						style="max-width: 14rem"
					></v-combobox>
					<v-combobox
						outlined
						class="mr-2"
						dense
						label="风味"
						v-model="filterFlavors"
						:items="flavorItems"
						multiple
						chips
						closable-chips
						hide-details
						clearable
						style="max-width: 14rem"
					></v-combobox>
					<v-btn color="primary" size="large" class="mr-2" @click="search">查询</v-btn>
					<v-icon class="mr-2" v-if="queryPairs.length" @click="reset">$close</v-icon>

					<span class="text-caption text-medium-emphasis">共 {{ total }} 条</span>
				</div>
			</v-form>
		</v-card-title>

		<v-data-table-server
			:headers="headers"
			:items="objects"
			@update:options="load"
			:items-length="parseInt(total)"
			:loading="loading"
			multi-sort
			:show-select="showSelect"
			:single-select="singleSelect"
			v-model="selected"
		>
			<template v-slot:top>
				<div class="d-flex justify-center align-center w-100" style="background-color: grey; color: white">
					<template v-if="!singleSelect && selected.length > 0">选中{{ selected.length }} 条</template>
					<slot name="batchActions" v-bind:selected="selected"></slot>
				</div>
			</template>

			<template #[`item.rawstock`]="{ item }">
				<a
					href="javascript:void(0)"
					style="text-decoration: underline; color: green"
					@click="goTo(`/bean/batchmanage?bean_id=${item.id}`)"
				>
					{{ item.rawstock }}
				</a>
			</template>
			<template #header.avg_score="{ column }">
				<span class="d-inline-flex align-center ga-1">
					{{ column.title }}
					<v-menu location="bottom" :close-on-content-click="false">
						<template #activator="{ props }">
							<v-btn v-bind="props" icon variant="text" size="x-small" @click.stop>
								<v-icon size="16" color="info">mdi-information-outline</v-icon>
							</v-btn>
						</template>
						<v-card max-width="360">
							<v-card-title class="text-body-1">平均杯测分 · 计算规则</v-card-title>
							<v-card-text class="text-body-2">
								<div>· 范围：最近 2 年内的有效评分，每条都计入（同一人多次评分不折叠）</div>
								<div>· 总分：8 个评分项之和；任一分项不在 0–15 或总分为 0 的记录不计</div>
								<div>
									· 汇总：有效评分不少于 {{ avgScoreMinCount }} 条才显示，对所有总分取平均（保留 1
									位小数）
								</div>
								<div>· 括号内为参与计算的评分条数，不足 {{ avgScoreMinCount }} 条显示 —</div>
							</v-card-text>
						</v-card>
					</v-menu>
				</span>
			</template>
			<template #item.avg_score="{ item }">
				<template v-if="item.avg_score != null">
					<span class="font-weight-medium">{{ formatAvgScore(item.avg_score) }}</span>
					<a
						href="javascript:void(0)"
						class="text-caption"
						style="text-decoration: underline"
						title="查看杯测评分"
						@click="goTo(`/cupping/scoremanage?bean_id=${item.id}`)"
					>
						({{ item.avg_score_count || 0 }}条)
					</a>
				</template>
				<span v-else class="text-medium-emphasis">
					—
					<a
						v-if="item.avg_score_count"
						href="javascript:void(0)"
						class="text-caption"
						style="text-decoration: underline"
						title="查看杯测评分"
						@click="goTo(`/cupping/scoremanage?bean_id=${item.id}`)"
					>
						({{ item.avg_score_count }}条)
					</a>
				</span>
			</template>
			<template #item.roastedstock="{ item }">
				<a
					href="javascript:void(0)"
					style="text-decoration: underline; color: brown; font-weight: bolder; font-size: 1.2rem"
					@click="goTo(`/roast/manage?bean_id=${item.id}`)"
				>
					{{ item.roastedstock }}
				</a>
			</template>
			<template v-slot:item.status="{ item }">
				<enum-chip kind="status" :value="item.status" />
			</template>
			<template v-slot:item.name="{ item }">{{ item.name }}</template>
			<template v-slot:item.owner="{ item }">
				{{ item.owner }}
				<template v-if="item.owner == 'other'">
					{{ item.otherowner }}
				</template>
			</template>
			<template v-slot:item.process="{ item }">
				{{ formatProcess(item.process, item.otherprocess) }}
			</template>
			<template v-slot:item.country="{ item }">
				{{ formatCountry(item.country) }}
			</template>
			<template v-slot:item.tags="{ item }">
				<template v-for="tag in displayTags(item)" :key="tag.id || tag.name">
					<v-chip size="x-small" class="ma-0 mr-1 mb-1" label variant="tonal">{{ tag.name }}</v-chip>
				</template>
			</template>

			<template v-slot:item.action="{ item }">
				<slot name="actions" v-bind="{ item }"></slot>
			</template>

			<template v-slot:footer>
				<slot name="batchActions" v-bind="{ selected }"></slot>
			</template>
		</v-data-table-server>
	</div>
</template>

<script>
import { clone, valuesIn, toPairsIn, filter, intersection } from 'lodash-es'
import qs from 'qs'
import EnumChip from '../common/EnumChip.vue'

export default {
	components: { EnumChip },
	props: {
		initQuery: { type: Object, default: null },
		title: { type: String, default: null },
		showSelect: { type: Boolean, default: false },
		singleSelect: { type: Boolean, default: true },
		headers: {
			type: Array,
			default: () => [
				{ title: '#', value: 'id' },
				{ title: '编码', align: 'center', sortable: true, value: 'code' },
				{ title: '名称', align: 'left', sortable: true, value: 'name' },
				{ title: '杯测分', align: 'center', sortable: false, value: 'avg_score' },
				{ title: '生豆库存', sortable: true, value: 'rawstock' },
				{ title: '熟豆库存', sortable: true, value: 'roastedstock' },
				{ title: '归属', sortable: true, value: 'owner' },
				{ title: '处理方式', sortable: true, value: 'process' },
				{ title: '产地', value: 'country' },
				{ title: '标签', value: 'tags', sortable: false },
				{ title: '状态', value: 'status' },
				{ title: '', align: 'left', sortable: false, value: 'action' }
			]
		}
	},
	data() {
		const qps = qs.parse(location.search, { ignoreQueryPrefix: true })
		return {
			query: qps,
			currentQuery: {}, //当前结果的查询条件
			objects: [],
			total: 0,
			avgScoreMinCount: 1, // 与后端 cupping.AvgScoreMinCount 同步，列表接口会回传
			loading: false,
			options: null, //当前选项（保持）
			refs: {},
			selected: [],
			errors: {},
			filterOrigins: [],
			filterProcesses: [],
			filterVarieties: [],
			filterFlavors: [],
			originItems: [],
			processItems: [],
			varietyItems: [],
			flavorItems: [],
			kindItems: [
				{ title: '单品', value: 'single' },
				{ title: '拼配', value: 'blend' }
			],
			countryLabelMap: {
				CN: '中国',
				ET: '埃塞俄比亚',
				VN: '越南',
				CO: '哥伦比亚',
				BR: '巴西',
				ID: '印度尼西亚',
				HN: '洪都拉斯',
				IN: '印度',
				UG: '乌干达',
				MX: '墨西哥',
				PE: '秘鲁',
				GT: '危地马拉',
				CR: '哥斯达黎加',
				NI: '尼加拉瓜',
				SV: '萨尔瓦多',
				TZ: '坦桑尼亚',
				KE: '肯尼亚',
				PG: '巴布亚新几内亚',
				VE: '委内瑞拉',
				DO: '多米尼加',
				MG: '马达加斯加',
				RW: '卢旺达',
				YE: '也门',
				PA: '巴拿马',
				OTHER: '其他'
			}
		}
	},
	mounted() {
		if (this.initQuery) {
			this.query = clone(this.initQuery)
		}
		this.hydrateFiltersFromQuery()
		this.$api.bean.meta('BeanType,BeanProcess,BeanStatus,BeanKind', (res) => {
			this.refs = res
			if (res.BeanKind?.length) {
				this.kindItems = res.BeanKind
			}
		})
		this.loadTagFilters()
		this.load()
	},
	computed: {
		queryValues() {
			return filter(valuesIn(this.query), (e) => !!e)
		},
		currentQueryValues() {
			return filter(valuesIn(this.currentQuery), (e) => !!e)
		},
		queryPairs() {
			return filter(toPairsIn(this.query), (e) => !!e[1])
		}
	},
	watch: {},
	methods: {
		hydrateFiltersFromQuery() {
			const split = (v) =>
				String(v || '')
					.split(',')
					.map((s) => s.trim())
					.filter(Boolean)
			this.filterOrigins = split(this.query.origins)
			this.filterProcesses = split(this.query.processes)
			this.filterVarieties = split(this.query.varieties)
			this.filterFlavors = split(this.query.flavors)
		},
		applyFiltersToQuery() {
			const join = (arr) => (arr && arr.length ? arr.join(',') : undefined)
			this.query.origins = join(this.filterOrigins)
			this.query.processes = join(this.filterProcesses)
			this.query.varieties = join(this.filterVarieties)
			this.query.flavors = join(this.filterFlavors)
		},
		loadTagFilters() {
			this.$http.get('/tag/getGroups', { params: { type: 'bean' } }).then((groups) => {
				const list = groups || []
				const byName = (name) => list.find((g) => g.name === name)
				const originGroup = byName('产地')
				const processGroup = byName('处理法')
				const varietyGroup = byName('品种')
				const flavorGroup = byName('风味')
				this.originItems = (originGroup?.tags || []).map((t) => ({
					title: this.countryLabelMap[t.name] || t.name,
					value: t.name
				}))
				const preset = ['日晒', '水洗', '蜜处理']
				const fromTags = (processGroup?.tags || []).map((t) => t.name)
				this.processItems = [...new Set([...preset, ...fromTags])]
				this.varietyItems = (varietyGroup?.tags || []).map((t) => t.name)
				this.flavorItems = (flavorGroup?.tags || []).map((t) => t.name)
			})
		},
		displayTags(item) {
			const tags = item.tags || []
			// 列表优先展示风味/品种，避免与产地列重复过多
			const preferred = tags.filter(
				(t) =>
					t.group?.name === '风味' ||
					t.group?.name === '品种' ||
					t.group_name === '风味' ||
					t.group_name === '品种'
			)
			return preferred.length ? preferred : tags.slice(0, 4)
		},
		formatAvgScore(v) {
			const n = Number(v)
			return Number.isNaN(n) ? '—' : n.toFixed(1)
		},
		formatProcess(process, otherProcess) {
			if (!process) return ''
			const map = { natural: '日晒', washed: '水洗', honey: '蜜处理', other: '其他' }
			const raw = String(process).trim()
			if (['natural', 'washed', 'honey', 'other'].includes(raw)) {
				if (raw === 'other') return otherProcess || '其他'
				return map[raw]
			}
			return raw
				.split(/[/、,，+;|]+/)
				.map((p) => p.trim())
				.filter(Boolean)
				.map((p) => map[p] || map[p.toLowerCase()] || p)
				.join('、')
		},
		formatCountry(raw) {
			if (!raw) return ''
			const map = {
				CN: '中国',
				ET: '埃塞俄比亚',
				VN: '越南',
				CO: '哥伦比亚',
				BR: '巴西',
				ID: '印度尼西亚',
				HN: '洪都拉斯',
				IN: '印度',
				UG: '乌干达',
				MX: '墨西哥',
				PE: '秘鲁',
				GT: '危地马拉',
				CR: '哥斯达黎加',
				NI: '尼加拉瓜',
				SV: '萨尔瓦多',
				TZ: '坦桑尼亚',
				KE: '肯尼亚',
				PG: '巴布亚新几内亚',
				VE: '委内瑞拉',
				DO: '多米尼加',
				MG: '马达加斯加',
				RW: '卢旺达',
				YE: '也门',
				PA: '巴拿马',
				china: '中国',
				ethiopia: '埃塞俄比亚',
				vietnam: '越南',
				colombia: '哥伦比亚',
				brazil: '巴西',
				indonesia: '印度尼西亚',
				honduras: '洪都拉斯',
				india: '印度',
				uganda: '乌干达',
				mexico: '墨西哥',
				peru: '秘鲁',
				guatemala: '危地马拉',
				costa_rica: '哥斯达黎加',
				nicaragua: '尼加拉瓜',
				salvador: '萨尔瓦多',
				tanzania: '坦桑尼亚',
				kenya: '肯尼亚',
				papua_new_guinea: '巴布亚新几内亚',
				venezuela: '委内瑞拉',
				dominican_republic: '多米尼加',
				madagascar: '马达加斯加',
				other: '其他'
			}
			return String(raw)
				.split(/[,，\s]+/)
				.filter(Boolean)
				.map((c) => map[c] || map[c.toUpperCase()] || map[c.toLowerCase()] || c)
				.join('、')
		},
		search() {
			this.applyFiltersToQuery()
			if (
				this.queryValues.length !== this.currentQueryValues.length ||
				intersection(this.queryValues, this.currentQueryValues).length !== this.queryValues.length
			) {
				//条件变化
				if (this.options.page === 1) {
					//当前如果是第一页，手动触发
					this.load()
				} else {
					this.options.page = 1 //当前不是第一页，修改后通过watcher触发
				}
			} else {
				//条件没变，忽略
				this.load()
			}
		},
		reset() {
			this.query = {}
			this.filterOrigins = []
			this.filterProcesses = []
			this.filterVarieties = []
			this.filterFlavors = []
			this.load()
		},
		load(options = null) {
			if (!this.options) this.options = {}
			if (options) {
				this.options = options
			}

			this.applyFiltersToQuery()
			let { sortBy, page, itemsPerPage } = this.options
			this.loading = true
			let qwp = clone(this.query)
			qwp.pageno = page
			qwp.pagesize = itemsPerPage
			qwp.sortBy = 'id'
			qwp.sortDesc = 'desc'
			if (sortBy && sortBy.length > 0) {
				qwp.sort = sortBy.map((e) => `${e.key} ${e.order}`).join(',')
				qwp.sortBy = sortBy[0]['key']
				qwp.sortDesc = sortBy[0]['order']
			}

			this.currentQuery = clone(this.query)
			this.$api.bean.search(
				qwp,
				(response) => {
					this.objects = response.objects
					this.total = response.total
					if (response.avg_score_min_count != null) {
						this.avgScoreMinCount = response.avg_score_min_count
					}
					this.loading = false
				},
				(error) => {
					this.errors = error.message
					this.loading = false
				}
			)
		}
	}
}
</script>

<style scoped>
.silent td {
	color: rgba(3, 3, 3, 0.5) !important;
}
</style>
